import { useState, useEffect } from 'react';
import { userPreferences } from '../utils/userPreferences';

interface UseThemeReturn {
    isDarkMode: boolean;
    toggleTheme: () => void;
}

/**
 * Custom hook for managing theme (dark/light mode)
 * Supports user preference, system preference, and persistence
 */
export function useTheme(): UseThemeReturn {
    const [isDarkMode, setIsDarkMode] = useState(false); // Will be set in useEffect

    // Initialize theme preference (user preference or system)
    useEffect(() => {
        const storedDarkMode = userPreferences.isDarkMode;

        if (storedDarkMode !== null) {
            // User has explicitly set a preference
            setIsDarkMode(storedDarkMode);
            console.log('🎨 [Theme] Using stored preference:', storedDarkMode ? 'dark' : 'light');
        } else {
            // Follow system preference
            const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
            setIsDarkMode(mediaQuery.matches);
            console.log('🎨 [Theme] Following system preference:', mediaQuery.matches ? 'dark' : 'light');

            const handleChange = (e: MediaQueryListEvent) => {
                // Only follow system changes if user hasn't set explicit preference
                if (userPreferences.isDarkMode === null) {
                    setIsDarkMode(e.matches);
                    console.log('🎨 [Theme] System preference changed:', e.matches ? 'dark' : 'light');
                }
            };

            mediaQuery.addEventListener('change', handleChange);
            return () => mediaQuery.removeEventListener('change', handleChange);
        }
    }, []);

    // Apply theme to HTML element to prevent white flashes on mobile
    useEffect(() => {
        const htmlElement = document.documentElement;
        const bodyElement = document.body;

        if (isDarkMode) {
            htmlElement.classList.add('theme-dark');
            htmlElement.classList.remove('theme-light');
            bodyElement.classList.add('theme-dark');
            bodyElement.classList.remove('theme-light');
        } else {
            htmlElement.classList.add('theme-light');
            htmlElement.classList.remove('theme-dark');
            bodyElement.classList.add('theme-light');
            bodyElement.classList.remove('theme-dark');
        }

        // Update theme-color meta tag for mobile browsers
        const themeColorMeta = document.querySelector('meta[name="theme-color"]');
        if (themeColorMeta) {
            themeColorMeta.setAttribute('content', isDarkMode ? '#111827' : '#f9fafb');
        }
    }, [isDarkMode]);

    const toggleTheme = () => {
        const newDarkMode = !isDarkMode;
        setIsDarkMode(newDarkMode);
        userPreferences.setDarkMode(newDarkMode);
        console.log('🎨 [Theme] User toggled to:', newDarkMode ? 'dark' : 'light');
    };

    return {
        isDarkMode,
        toggleTheme,
    };
}
